import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, History, AlertTriangle, FileClock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const MONTHS = [
  ['01', 'January'], ['02', 'February'], ['03', 'March'], ['04', 'April'],
  ['05', 'May'], ['06', 'June'], ['07', 'July'], ['08', 'August'],
  ['09', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
];

const HistoricalTaxReport = () => {
  const now = new Date();
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 6; y--) years.push(String(y));

  const [periodType, setPeriodType] = useState('month'); // month | quarter | year
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [quarter, setQuarter] = useState('1');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildPeriod = useCallback(() => {
    if (periodType === 'month') return `${year}-${month}`;
    if (periodType === 'quarter') return `${year}-Q${quarter}`;
    return `${year}`;
  }, [periodType, year, month, quarter]);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const period = buildPeriod();
      const res = await axios.get(
        `${BACKEND_URL}/api/accounting/reports/gst-tcs/historical?period=${encodeURIComponent(period)}`,
      );
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate historical report');
    } finally {
      setLoading(false);
    }
  }, [buildPeriod]);

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const downloadCSV = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const marker = `Report generated ${(data.generatedAt || '').slice(0, 10)}, for historical period ${data.periodLabel}, reference only`;
    const headers = [
      'Bucket', 'Invoice Date', 'Pax Name', 'Trip ID', 'Invoice #',
      'PAN', 'Passport', 'GSTIN', 'Address', 'Customer Type', 'B2B/B2C',
      'Destination', 'Place of Supply', 'Supply Type',
      'Base', 'CGST', 'SGST', 'IGST', 'GST', 'TCS', 'Total',
      'Missing Fields', 'Edited',
    ];
    const lines = [esc(marker), '', headers.join(',')];
    for (const r of (data.rows || [])) {
      lines.push([
        esc(r.bucket), esc(r.invoiceDate), esc(r.paxName), esc(r.displayTripId || r.tripId),
        esc(r.invoiceNumber), esc(r.panNumber), esc(r.passportNumber), esc(r.gstin),
        esc(r.address), esc(r.customerType), esc(r.businessType), esc(r.destination),
        esc(r.placeOfSupply), esc(r.supplyType),
        r.base, r.cgst, r.sgst, r.igst, r.gst, r.tcs, r.total,
        esc((r.missingFields || []).join(' | ')), r.edited ? 'YES' : '',
      ].join(','));
    }
    lines.push('');
    lines.push(esc(data.disclaimer || 'Reference only — generated from CRM records.'));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historical-gst-tcs-${buildPeriod()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${(data.rows || []).length} rows`);
  };

  const totals = data?.totals || {};
  const incomplete = data?.dataCompleteness?.incompleteCount ?? 0;
  const editedCount = data?.editedInvoices?.count ?? 0;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="historical-tax-report-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <History className="h-6 w-6 text-indigo-600" />
            Historical Tax Report
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Reference-only GST/TCS view for a past filing period, generated from current CRM records.
          </p>
        </div>
        <Button variant="outline" onClick={downloadCSV} data-testid="export-csv-btn" className="gap-2" disabled={!data}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </header>

      {/* Period picker */}
      <Card className="p-4 flex flex-wrap gap-3 items-end" data-testid="period-picker">
        <div>
          <Label className="text-xs">Period type</Label>
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-36 mt-1" data-testid="period-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Year</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28 mt-1" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {periodType === 'month' && (
          <div>
            <Label className="text-xs">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40 mt-1" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {periodType === 'quarter' && (
          <div>
            <Label className="text-xs">Quarter</Label>
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="w-28 mt-1" data-testid="quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['1', '2', '3', '4'].map((q) => <SelectItem key={q} value={q}>Q{q}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={generate} disabled={loading} data-testid="generate-btn" className="gap-2">
          <FileClock className="h-4 w-4" /> {loading ? 'Generating…' : 'Generate'}
        </Button>
      </Card>

      {/* Reference-only disclaimer */}
      {data && (
        <div
          className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg"
          data-testid="disclaimer-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>Reference only.</strong> {data.disclaimer}</span>
        </div>
      )}

      {/* Totals + flags strip */}
      {data && (
        <Card className="grid grid-cols-2 md:grid-cols-6 divide-x divide-slate-100" data-testid="totals-strip">
          <Cell label="Rows" value={totals.rows ?? 0} />
          <Cell label="Base" value={fmtINR(totals.base)} />
          <Cell label="GST" value={fmtINR(totals.gst)} tone="emerald" />
          <Cell label="TCS" value={fmtINR(totals.tcs)} tone="emerald" />
          <Cell label="Incomplete" value={incomplete} tone={incomplete ? 'rose' : 'muted'} testid="incomplete-chip" />
          <Cell label="Edited" value={editedCount} tone={editedCount ? 'amber' : 'muted'} testid="edited-chip" />
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm" data-testid="report-loading">Loading…</div>
      ) : !data || (data.rows || []).length === 0 ? (
        <Card className="text-center py-12 text-slate-400 text-sm" data-testid="empty-state">
          No invoices found for {data?.periodLabel || 'this period'}.
        </Card>
      ) : (
        <Card className="overflow-x-auto" data-testid="rows-table">
          <table className="w-full">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <Th>Month</Th><Th>Inv Date</Th><Th>Pax Name</Th><Th>Trip ID</Th><Th>Invoice #</Th>
                <Th>PAN</Th><Th>Passport</Th><Th>GSTIN</Th><Th>Address</Th><Th>Customer</Th>
                <Th>B2B/B2C</Th><Th>Destination</Th><Th>Place of Supply</Th><Th>Supply</Th>
                <Th align="right">Base</Th><Th align="right">CGST</Th><Th align="right">SGST</Th>
                <Th align="right">IGST</Th><Th align="right">GST</Th><Th align="right">TCS</Th>
                <Th align="right">Total</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(data.rows || []).map((r, i) => (
                <tr key={i} data-testid={`hist-row-${i}`} className="text-xs hover:bg-slate-50">
                  <Td>{r.bucket}</Td>
                  <Td className="font-mono whitespace-nowrap">{r.invoiceDate || '—'}</Td>
                  <Td>{r.paxName || '—'}</Td>
                  <Td className="font-mono">{r.displayTripId || r.tripId || '—'}</Td>
                  <Td className="font-mono">{r.invoiceNumber}</Td>
                  <Td className={`font-mono ${!r.panNumber ? 'text-rose-600' : ''}`}>{r.panNumber || 'NO PAN'}</Td>
                  <Td className="font-mono">{r.passportNumber || '—'}</Td>
                  <Td className="font-mono">{r.gstin || '—'}</Td>
                  <Td className="max-w-[160px] truncate" title={r.address || ''}>{r.address || '—'}</Td>
                  <Td>{r.customerType || '—'}</Td>
                  <Td>{r.businessType || '—'}</Td>
                  <Td>{r.destination || '—'}</Td>
                  <Td>{r.placeOfSupply || '—'}</Td>
                  <Td className="capitalize">{r.supplyType || '—'}</Td>
                  <Td align="right" className="font-mono">{fmtNum(r.base)}</Td>
                  <Td align="right" className="font-mono">{fmtNum(r.cgst)}</Td>
                  <Td align="right" className="font-mono">{fmtNum(r.sgst)}</Td>
                  <Td align="right" className="font-mono">{fmtNum(r.igst)}</Td>
                  <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.gst)}</Td>
                  <Td align="right" className="font-mono text-emerald-700">{fmtNum(r.tcs)}</Td>
                  <Td align="right" className="font-mono font-semibold">{fmtNum(r.total)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1" data-testid={`hist-row-${i}-status`}>
                      {(r.missingFields || []).map((m) => (
                        <span key={m} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-semibold" data-testid={`missing-${m}`}>
                          No {m}
                        </span>
                      ))}
                      {r.edited && (
                        <span
                          className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-semibold"
                          title={r.editedNote || 'Edited after the original period'}
                          data-testid="edited-badge"
                        >
                          Edited
                        </span>
                      )}
                      {!(r.missingFields || []).length && !r.edited && (
                        <span className="text-emerald-600 text-[10px]">OK</span>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

function Cell({ label, value, tone, testid }) {
  const colorClass = tone === 'emerald' ? 'text-emerald-700'
    : tone === 'rose' ? 'text-rose-600 font-bold'
    : tone === 'amber' ? 'text-amber-700 font-bold'
    : tone === 'muted' ? 'text-slate-400'
    : 'text-slate-700';
  return (
    <div className="p-4" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-mono mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}

const Th = ({ children, align }) => (
  <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, align, className = '' }) => (
  <td className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>{children}</td>
);

export default HistoricalTaxReport;
